import React from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Sonner, ThemeProvider, ErrorBoundary, I18nProvider, TooltipProvider } from "@etherisc/ui-kit";
// Import UI kit styles FIRST (contains component styles and proper theme variables)  
import "@etherisc/ui-kit/dist/style.css";
// Import Leaflet CSS for map components
import "leaflet/dist/leaflet.css";
// Import our custom overrides LAST (only for application-specific styling)
import "./index.css";

import { LoginPage } from "./pages/LoginPage";
import { SignupPage } from "./pages/SignupPage";
import { SetPasswordPage } from "./pages/auth/SetPassword";
import { ResetPasswordPage } from "./pages/auth/ResetPassword";
import { ForgotPasswordPage } from "./pages/auth/ForgotPassword";
import { DashboardPage } from "./pages/DashboardPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ProtectedRoute } from "./components/ProtectedRoute";
// Import admin page implementations
import { UserManagement } from "./pages/admin/UserManagement";
import { CreateUserPage } from "./pages/admin/CreateUser";
import { EditUserPage } from "./pages/admin/EditUser";
import { OrganizationManagement } from "./pages/admin/OrganizationManagement";
import { CreateOrganization } from "./pages/admin/CreateOrganization";
import { EditOrganization } from "./pages/admin/EditOrganization";
import { OrganizationDetailPage } from "./pages/admin/OrganizationDetailPage";
import { RoleManagement } from "./pages/admin/RoleManagement";
import { FarmerManagement } from "./pages/admin/FarmerManagement";
import { AdministrativeUnitsManagement } from "./pages/admin/AdministrativeUnitsManagement";
import { CreateFarmer } from "./pages/admin/CreateFarmer";
import { FarmerDetailPage } from "./pages/admin/FarmerDetailPage";
import { EditFarmer } from "./pages/admin/EditFarmer";
import { ShapefileManagementPage } from "./pages/admin/ShapefileManagementPage";
import { CoverageManagement } from "./pages/admin/CoverageManagement";
import { DatabaseManagement } from "./pages/admin/DatabaseManagement";
import { AbilityProviderWrapper } from "./components/AbilityProviderWrapper";
import { JobListPage } from "./pages/admin/JobListPage";
import { JobDetailPage } from "./pages/admin/JobDetailPage";
import FilesListPage from "./pages/admin/FilesListPage";
import { QuoteManagement } from "./pages/admin/QuoteManagement";
import { ProductManagement } from "./pages/admin/ProductManagement";
import { CreateProduct } from "./pages/admin/CreateProduct";
import { ProductDetail } from "./pages/admin/ProductDetail";
import { EditProduct } from "./pages/admin/EditProduct";
import { CreateQuote } from "./pages/admin/CreateQuote";
import { QuoteDetail } from "./pages/admin/QuoteDetail";
import { EditQuote } from "./pages/admin/EditQuote";
import { QuoteDocumentManagement } from "./pages/admin/QuoteDocumentManagement";
import { DocumentTemplateManagement } from "./pages/admin/DocumentTemplateManagement";
import { NotificationManagement } from "./pages/admin/NotificationManagement";
import { ClaimsManagement } from "./pages/admin/ClaimsManagement";
import { ClaimsDetail } from "./pages/admin/ClaimsDetail";
import { ClaimsPayments } from "./pages/admin/ClaimsPayments";
import { AuditTrail } from "./pages/admin/AuditTrail";
import { AuditDetail } from "./pages/admin/AuditDetail";
// import { TestQuoteWizardPage } from "./pages/TestQuoteWizardPage";
import { ErrorBoundary as CustomErrorBoundary } from './components/ErrorBoundary';

// Import ledger pages
import { LedgerDashboard } from "./pages/ledger/LedgerDashboard";
import { JournalManagement } from "./pages/ledger/JournalManagement";
import { JournalDetail } from "./pages/ledger/JournalDetail";
import { TrialBalanceReport } from "./pages/ledger/TrialBalanceReport";
import { ChartOfAccounts } from "./pages/ledger/ChartOfAccounts";
import { PeriodManagement } from "./pages/ledger/PeriodManagement";
// Insurance pages
import { PremiumManagement } from "./pages/insurance/PremiumManagement";
import { InsuranceReports } from "./pages/insurance/InsuranceReports";
import { BatchManagement } from "./pages/insurance/BatchManagement";
import { AnalyticsDashboard } from "./pages/insurance/AnalyticsDashboard";
import { StateMachineDemo } from "./pages/state-machine/StateMachineDemo";
import { ProcessManagerPage } from "./pages/admin/ProcessManagerPage";
import { PaymentManagement } from "./pages/admin/PaymentManagement";
import { MockPaymentApiPage } from "./pages/dev/MockPaymentApiPage";

// Import new AppLayout wrapper
import { AppLayout } from "./components/layout/AppLayout";

// DaisyUI theme is required for UI kit CSS variables to work
document.documentElement.setAttribute("data-theme", "light");

// Set browser tab title with environment suffix
// Try build-time environment variable first, then fetch from API
const setBrowserTitle = async () => {
  const baseTitle = "MyUbuntu";
  let environment = import.meta.env.VITE_ENVIRONMENT;
  
  // If no build-time environment, fetch from health API
  if (!environment) {
    try {
      const response = await fetch('/health');
      if (response.ok) {
        const data = await response.json();
        environment = data.environment;
      }
    } catch {
      // Fallback to no suffix if API fails
      environment = 'production';
    }
  }
  
  // Set title based on environment
  if (environment === "testing") {
    document.title = `${baseTitle} [Testing]`;
  } else if (environment === "staging") {
    document.title = `${baseTitle} [Staging]`;
  } else {
    document.title = baseTitle; // Production - no suffix
  }
};

// Set title immediately with build-time info if available, then update from API if needed
setBrowserTitle();

// Create QueryClient for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 2,
    },
  },
});

// For demo purposes - in production this would be from environment variables
// const GOOGLE_CLIENT_ID = "demo-client-id-for-development";

const router = createBrowserRouter([
  {
    path: "/",
    element: <LoginPage />,
  },
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/signup",
    element: <SignupPage />,
  },
  {
    path: "/set-password",
    element: <SetPasswordPage />,
  },
  {
    path: "/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/dashboard",
    element: (
      <ProtectedRoute>
        <AppLayout>
          <DashboardPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/users",
    element: (
      <ProtectedRoute permissions={['users:read']}>
        <AppLayout>
          <UserManagement />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/roles",
    element: (
      <ProtectedRoute permissions={['users:assign_roles']}>
        <AppLayout>
          <RoleManagement />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/users/create",
    element: (
      <ProtectedRoute permissions={['users:create']}>
        <AppLayout>
          <CreateUserPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/users/:id/edit",
    element: (
      <ProtectedRoute permissions={['users:update']}>
        <AppLayout>
          <EditUserPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/organizations",
    element: (
      <ProtectedRoute permissions={['organizations:read']}>
        <AppLayout>
          <OrganizationManagement />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/organizations/create",
    element: (
      <ProtectedRoute permissions={['organizations:create']}>
        <AppLayout>
          <CreateOrganization />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/organizations/:id",
    element: (
      <ProtectedRoute permissions={['organizations:read']}>
        <AppLayout>
          <OrganizationDetailPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/organizations/:id/edit",
    element: (
      <ProtectedRoute permissions={['organizations:update']}>
        <AppLayout>
          <EditOrganization />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/farmers",
    element: (
      <ProtectedRoute permissions={['farmers:read']}>
        <AppLayout>
          <FarmerManagement />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/farmers/create",
    element: (
      <ProtectedRoute permissions={['farmers:create']}>
        <AppLayout>
          <CreateFarmer />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/farmers/:id",
    element: (
      <ProtectedRoute permissions={['farmers:read']}>
        <AppLayout>
          <FarmerDetailPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/farmers/:id/edit",
    element: (
      <ProtectedRoute permissions={['farmers:update']}>
        <AppLayout>
          <EditFarmer />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/administrative-units",
    element: (
      <ProtectedRoute permissions={['system:configure']}>
        <AppLayout>
          <AdministrativeUnitsManagement />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/shapefiles",
    element: (
      <ProtectedRoute>
        <AppLayout>
          <ShapefileManagementPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/coverage",
    element: (
      <ProtectedRoute>
        <AppLayout>
          <CoverageManagement />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/database",
    element: (
      <ProtectedRoute permissions={['system:configure']}>
        <AppLayout>
          <DatabaseManagement />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/jobs",
    element: (
      <ProtectedRoute>
        <AppLayout>
          <JobListPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/jobs/:jobId",
    element: (
      <ProtectedRoute>
        <AppLayout>
          <JobDetailPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/files",
    element: (
      <ProtectedRoute>
        <AppLayout>
          <FilesListPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/quotes",
    element: (
      <ProtectedRoute permissions={['quotes:read']}>
        <AppLayout>
          <QuoteManagement />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/products",
    element: (
      <ProtectedRoute permissions={['products:read']}>
        <AppLayout>
          <ProductManagement />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/products/create",
    element: (
      <ProtectedRoute permissions={['products:create']}>
        <AppLayout>
          <CreateProduct />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/products/:id",
    element: (
      <ProtectedRoute permissions={['products:read']}>
        <AppLayout>
          <ProductDetail />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/products/:id/edit",
    element: (
      <ProtectedRoute permissions={['products:update']}>
        <AppLayout>
          <EditProduct />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/quotes/create",
    element: (
      <ProtectedRoute permissions={['quotes:create']}>
        <AppLayout>
          <CreateQuote />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/quotes/:id",
    element: (
      <ProtectedRoute permissions={['quotes:read']}>
        <AppLayout>
          <QuoteDetail />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/quotes/:id/edit",
    element: (
      <ProtectedRoute permissions={['quotes:update']}>
        <AppLayout>
          <EditQuote />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/quotes/:id/documents",
    element: (
      <ProtectedRoute permissions={['quotes:read']}>
        <AppLayout>
          <QuoteDocumentManagement />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  // Claims routes
  {
    path: "/claims",
    element: (
      <ProtectedRoute permissions={['claims:read']}>
        <AppLayout>
          <ClaimsManagement />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/claims/:quoteId",
    element: (
      <ProtectedRoute permissions={['claims:read']}>
        <AppLayout>
          <ClaimsDetail />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/claims-payments",
    element: (
      <ProtectedRoute permissions={['claims:read']}>
        <AppLayout>
          <ClaimsPayments />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  // Audit trail routes
  {
    path: "/admin/audit",
    element: (
      <ProtectedRoute permissions={['system:configure']}>
        <AppLayout>
          <AuditTrail />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/audit/:id",
    element: (
      <ProtectedRoute permissions={['system:configure']}>
        <AppLayout>
          <AuditDetail />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/document-templates",
    element: (
      <AppLayout>
        <ProtectedRoute>
          <CustomErrorBoundary>
            <DocumentTemplateManagement />
          </CustomErrorBoundary>
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/notifications",
    element: (
      <AppLayout>
        <ProtectedRoute>
          <CustomErrorBoundary>
            <NotificationManagement />
          </CustomErrorBoundary>
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  // Ledger routes
  {
    path: "/ledger",
    element: (
      <ProtectedRoute permissions={['ledger:read']}>
        <AppLayout>
          <LedgerDashboard />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/ledger/journals",
    element: (
      <ProtectedRoute permissions={['ledger:read']}>
        <AppLayout>
          <JournalManagement />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/ledger/journals/:id",
    element: (
      <ProtectedRoute permissions={['ledger:read']}>
        <AppLayout>
          <JournalDetail />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/ledger/reports/trial-balance",
    element: (
      <ProtectedRoute permissions={['ledger:reports']}>
        <AppLayout>
          <TrialBalanceReport />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/ledger/accounts",
    element: (
      <ProtectedRoute permissions={['ledger:read']}>
        <AppLayout>
          <ChartOfAccounts />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/ledger/periods",
    element: (
      <ProtectedRoute permissions={['ledger:read']}>
        <AppLayout>
          <PeriodManagement />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  // Insurance routes
  {
    path: "/insurance/premiums",
    element: (
      <ProtectedRoute permissions={['ledger:write']}>
        <AppLayout>
          <PremiumManagement />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
    {
      path: "/insurance/reports",
      element: (
        <ProtectedRoute permissions={['ledger:reports']}>
          <AppLayout>
            <InsuranceReports />
          </AppLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/insurance/batch-management",
      element: (
        <ProtectedRoute permissions={['ledger:write']}>
          <AppLayout>
            <BatchManagement />
          </AppLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/insurance/analytics",
      element: (
        <ProtectedRoute permissions={['ledger:reports']}>
          <AppLayout>
            <AnalyticsDashboard />
          </AppLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/state-machine-demo",
      element: (
        <ProtectedRoute permissions={['users:read']}>
          <AppLayout>
            <StateMachineDemo />
          </AppLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/dev/mock-payment-api",
      element: (
        <ProtectedRoute permissions={['users:read']}>
          <AppLayout>
            <MockPaymentApiPage />
          </AppLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/processes",
      element: (
        <ProtectedRoute permissions={['users:read']}>
          <AppLayout>
            <ProcessManagerPage />
          </AppLayout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/payments",
      element: (
        <ProtectedRoute permissions={['payments:read']}>
          <AppLayout>
            <PaymentManagement />
          </AppLayout>
        </ProtectedRoute>
      ),
    },
  {
    path: "*",
    element: <NotFoundPage />,
  },
]);

const container = document.getElementById("root");
if (!container) throw new Error("Failed to find the root element");

const root = createRoot(container);
root.render(
  <React.StrictMode>
    {/* <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}> */}
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <I18nProvider>
          <ThemeProvider>
            <TooltipProvider>
              <AbilityProviderWrapper>
                <RouterProvider router={router} />
              </AbilityProviderWrapper>
            </TooltipProvider>
            <Sonner
              position="bottom-center"
              richColors={true}
              expand={true}
              duration={4000}
            />
          </ThemeProvider>
        </I18nProvider>
      </ErrorBoundary>
    </QueryClientProvider>
    {/* </GoogleOAuthProvider> */}
  </React.StrictMode>,
);
